import React, { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Field, Form } from "react-final-form";
import {
  CURRENCIES,
  IServiceDto,
  isRequired,
  RoutesEnum,
  userRoles,
} from "../../../shared";
import { useAppSelector } from "../../../hooks";
import { IServiceForm } from "./serviceForm.interface";
import styles from "./serviceForm.module.scss";
import { useNavigate } from "react-router-dom";
import { Button, TableDeleteButton } from "../../Buttons";

const validateDiscountRules = (value: any, allValues: any) => {
  if (!allValues.isOnline) return undefined;

  if (
    value?.length > 0 &&
    value.some((rule: any) => !rule.from || rule.from === 0)
  ) {
    return "formErrors.from_required";
  }
  if (
    value?.length > 0 &&
    value.some((rule: any) => !rule.price || rule.price === 0)
  ) {
    return "formErrors.price_required";
  }
  return undefined;
};

const defaultInitialValues: IServiceDto = {
  title: "",
  isRelatedToExercise: false,
  isOnline: false,
  description: "",
  duration: 0,
  price: 0,
  discountRules: [
    { from: 1, to: 5, price: 10 },
    { from: 6, to: 15, price: 9 },
    { from: 16, to: 30, price: 8 },
    { from: 31, to: 60, price: 7 },
    { from: 61, to: 90, price: 6 },
  ],
  numberOfVisitsAtTime: 1,
  currency: CURRENCIES[0].value,
};

export const ServiceForm: FC<IServiceForm<IServiceDto>> = ({
  onSubmit,
  initialValues = defaultInitialValues,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentUser = useAppSelector(
    ({ userReducer }) => userReducer.currentUser,
  );

  const [iconPreview, setIconPreview] = useState<string | File[]>();
  const [backgroundPreview, setBackgroundPreview] = useState<string | File[]>();
  const [articlePreview, setArticlePreview] = useState<string | File[]>();

  const calculateFromValue = (rules: any[], index: number): number => {
    if (index === 0) return 1;
    const prevRule = rules[index - 1];
    return prevRule && prevRule.to ? prevRule.to + 1 : 1;
  };

  const onSelectFile = (e: FileList | null, fieldName: string) => {
    const setters: Record<
      string,
      React.Dispatch<React.SetStateAction<string | File[] | undefined>>
    > = {
      iconImage: setIconPreview,
      backgroundImage: setBackgroundPreview,
      articleImage: setArticlePreview,
    };

    const value = e && e[0] ? URL.createObjectURL(e[0]) : "";
    const setter = setters[fieldName];

    if (setter) {
      setter(value);
    }
  };

  useEffect(() => {
    if (initialValues?.iconLink) {
      setIconPreview(initialValues?.iconLink);
    }
    if (initialValues?.backgroundLink) {
      setBackgroundPreview(initialValues?.backgroundLink);
    }
    if (initialValues?.articleImageLink) {
      setArticlePreview(initialValues?.articleImageLink);
    }
  }, [initialValues]);

  useEffect(() => {
    if (currentUser?.role !== userRoles.admin) {
      navigate(`${RoutesEnum.admin}${RoutesEnum.services}`);
    }
  }, [currentUser?.role, navigate]);

  return (
    <div className={styles.form_wrap}>
      <Form
        onSubmit={onSubmit}
        initialValues={initialValues}
        render={({ handleSubmit, form, submitting, pristine, values }) => (
          <form onSubmit={handleSubmit}>
            <Field name="title" validate={isRequired}>
              {({ input, meta }) => (
                <div
                  className={`${styles.input_wrap} ${
                    meta.error && meta.touched ? styles.form_input_error : ""
                  }`}
                >
                  <label htmlFor="title">{t("adminPanel.form.title")}*</label>
                  <input
                    {...input}
                    type="text"
                    placeholder={t("adminPanel.form.title")}
                    id="title"
                  />
                  {meta.error && meta.touched && <span>{t(meta.error)}</span>}
                </div>
              )}
            </Field>
            <div className={styles.checkbox_container}>
              <div className={styles.checkbox_field_wrap}>
                <Field
                  id="isRelatedToExercise"
                  name="isRelatedToExercise"
                  component="input"
                  type="checkbox"
                  defaultValue={false}
                  disabled={initialValues?.title ? true : false}
                />
                <label
                  htmlFor="isRelatedToExercise"
                  className={initialValues?.title ? styles.isDisabled : ""}
                >
                  {t("adminPanel.form.isRelatedToExercise")}
                </label>
              </div>
              {values.isRelatedToExercise &&
                !initialValues?.isRelatedToExercise && (
                  <span className={styles.isRelatedToExercise}>
                    {t("adminPanel.form.youCanNotChangeAfterSave")}
                  </span>
                )}
            </div>
            <div className={styles.checkbox_container}>
              <div className={styles.checkbox_field_wrap}>
                <Field
                  id="isOnline"
                  name="isOnline"
                  component="input"
                  type="checkbox"
                  defaultValue={false}
                  disabled={initialValues?.title ? true : false}
                />
                <label
                  htmlFor="isOnline"
                  className={initialValues?.title ? styles.isDisabled : ""}
                >
                  {t("adminPanel.form.isOnline")}
                </label>
              </div>
              {values.isOnline && !initialValues?.isOnline && (
                <span className={styles.isOnline}>
                  {t("adminPanel.form.youCanNotChangeAfterSave")}
                </span>
              )}
            </div>
            <Field name="description" validate={isRequired}>
              {({ input, meta }) => (
                <div
                  className={`${styles.input_wrap} ${
                    meta.error && meta.touched ? styles.form_input_error : ""
                  }`}
                >
                  <label htmlFor="description">
                    {t("adminPanel.form.shortDescription")}*
                  </label>
                  <textarea
                    {...input}
                    id="description"
                    placeholder={t("adminPanel.form.shortDescription")}
                  />
                  {meta.error && meta.touched && <span>{t(meta.error)}</span>}
                </div>
              )}
            </Field>
            <div className={styles.duration_price_wrap}>
              <Field name="duration" validate={isRequired}>
                {({ input, meta }) => (
                  <div
                    className={`${[styles.input_wrap, styles.duration_wrap].join(" ")}  ${
                      meta.error && meta.touched ? styles.form_input_error : ""
                    }`}
                  >
                    <label htmlFor="duration">
                      {t("adminPanel.form.duration")}*
                    </label>
                    <div className={styles.duration_input_wrap}>
                      <select {...input} id="duration">
                        <option value="">
                          {t("adminPanel.form.duration")}
                        </option>
                        <option value={15}>
                          15 {t("adminPanel.form.minutes")}
                        </option>
                        <option value={30}>
                          30 {t("adminPanel.form.minutes")}
                        </option>
                        <option value={45}>
                          45 {t("adminPanel.form.minutes")}
                        </option>
                        <option value={60}>
                          60 {t("adminPanel.form.minutes")}
                        </option>
                      </select>
                    </div>
                    {meta.error && meta.touched && <span>{t(meta.error)}</span>}
                  </div>
                )}
              </Field>
              <div className={styles.price_wrap}>
                <Field name="price" validate={isRequired}>
                  {({ input, meta }) => (
                    <div
                      className={`${styles.input_wrap} ${
                        meta.error && meta.touched
                          ? styles.form_input_error
                          : ""
                      }`}
                    >
                      <label htmlFor="price">
                        {t("adminPanel.form.price")}*
                      </label>
                      <input
                        {...input}
                        id="price"
                        type="number"
                        placeholder={t("adminPanel.form.price")}
                        min={0}
                      />
                      {meta.error && meta.touched && (
                        <span>{t(meta.error)}</span>
                      )}
                    </div>
                  )}
                </Field>
                <Field name="currency" validate={isRequired}>
                  {({ input, meta }) => (
                    <div
                      className={`${styles.input_wrap} ${
                        meta.error && meta.touched
                          ? styles.form_input_error
                          : ""
                      }`}
                    >
                      <label htmlFor="currency">
                        {t("adminPanel.form.currency")}*
                      </label>
                      <select {...input} id="currency">
                        {CURRENCIES.map((currency) => (
                          <option key={currency.value} value={currency.value}>
                            {currency.title}
                          </option>
                        ))}
                      </select>
                      {meta.error && meta.touched && (
                        <span>{t(meta.error)}</span>
                      )}
                    </div>
                  )}
                </Field>
              </div>
            </div>
            {values.isOnline && values.isRelatedToExercise && (
              <div className={`${styles.discount_rules_container}`}>
                <div className={styles.discount_rules_label}>
                  {t("adminPanel.form.discountRules")}
                </div>
                <Field name="discountRules" validate={validateDiscountRules}>
                  {({ input, meta }) => (
                    <>
                      {(input.value || []).map((rule: any, index: number) => {
                        const calculatedFrom = calculateFromValue(
                          input.value,
                          index,
                        );
                        if (rule.from !== calculatedFrom) {
                          const newRules = [...input.value];
                          newRules[index] = {
                            ...newRules[index],
                            from: calculatedFrom,
                          };
                          setTimeout(() => input.onChange(newRules), 0);
                        }

                        return (
                          <div
                            key={index}
                            className={`${styles.discount_rule_item} ${
                              meta.error && meta.touched
                                ? styles.form_input_error
                                : ""
                            }`}
                          >
                            <div className={styles.input_wrap}>
                              <label htmlFor={`from-${index}`}>
                                {t("adminPanel.form.from")}
                              </label>
                              <input
                                id={`from-${index}`}
                                type="number"
                                value={calculatedFrom}
                                onChange={(e) => {}}
                                placeholder={t("adminPanel.form.from")}
                                min={0}
                                step={1}
                                disabled={true}
                                className={styles.disabled_input}
                              />
                            </div>
                            <div className={styles.input_wrap}>
                              <label htmlFor={`to-${index}`}>
                                {t("adminPanel.form.to")}
                              </label>
                              <input
                                id={`to-${index}`}
                                type="number"
                                value={rule.to || ""}
                                onChange={(e) => {
                                  const newRules = [...input.value];
                                  newRules[index] = {
                                    ...newRules[index],
                                    to: e.target.value
                                      ? parseInt(e.target.value)
                                      : undefined,
                                  };
                                  if (
                                    index < newRules.length - 1 &&
                                    e.target.value
                                  ) {
                                    newRules[index + 1] = {
                                      ...newRules[index + 1],
                                      from: parseInt(e.target.value) + 1,
                                    };
                                  }
                                  input.onChange(newRules);
                                }}
                                placeholder={t("adminPanel.form.to")}
                                min={rule.from || 0}
                                step={1}
                              />
                            </div>
                            <div
                              className={`${styles.input_wrap} ${styles.price_per_session_wrap}`}
                            >
                              <label htmlFor={`pricePerSession-${index}`}>
                                {t("adminPanel.form.pricePerSession")}
                              </label>
                              <input
                                id={`pricePerSession-${index}`}
                                type="number"
                                value={rule.price || ""}
                                onChange={(e) => {
                                  const newRules = [...input.value];
                                  newRules[index] = {
                                    ...newRules[index],
                                    price:
                                      parseFloat(
                                        parseFloat(e.target.value).toFixed(2),
                                      ) || 0,
                                  };
                                  input.onChange(newRules);
                                }}
                                placeholder={t(
                                  "adminPanel.form.pricePerSession",
                                )}
                                min={0}
                                step={0.01}
                              />
                            </div>
                            <TableDeleteButton
                              onClick={() => {
                                const newRules = input.value.filter(
                                  (_: any, i: number) => i !== index,
                                );
                                input.onChange(newRules);
                              }}
                              title="adminPanel.form.deleteDiscountRule"
                            />
                          </div>
                        );
                      })}
                      {meta.error && meta.touched && (
                        <p className={styles.error_message}>{t(meta.error)}</p>
                      )}
                      <Button
                        type="button"
                        className={"green_outlined"}
                        customStyle={styles.add_discount_rule_button}
                        label={t("adminPanel.form.addDiscountRule")}
                        onClick={() => {
                          const currentRules = input.value || [];
                          const lastRule =
                            currentRules[currentRules.length - 1];
                          const newFrom = lastRule?.to
                            ? lastRule.to + 1
                            : currentRules.length === 0
                              ? 1
                              : 0;
                          const newRules = [
                            ...currentRules,
                            { from: newFrom, price: 0 },
                          ];
                          input.onChange(newRules);
                        }}
                      />
                    </>
                  )}
                </Field>
              </div>
            )}
            <Field name="numberOfVisitsAtTime" validate={isRequired}>
              {({ input, meta }) => {
                return (
                  <div
                    className={`${[
                      styles.input_wrap,
                      styles.numberOfVisits_wrap,
                    ].join(
                      " ",
                    )}  ${meta.error && meta.touched ? styles.form_input_error : ""}`}
                  >
                    <label htmlFor="numberOfVisitsAtTime">
                      {t("adminPanel.form.numberOfVisitors")}*
                    </label>
                    <div className={styles.numberOfVisits_input_wrap}>
                      <input
                        {...input}
                        id="numberOfVisitsAtTime"
                        type="number"
                        placeholder={t("adminPanel.form.numberOfVisitors")}
                        // value={currentValue}
                        onChange={(e) => {
                          input.onChange(e.target.value);
                        }}
                        // disabled={!values.isRelatedToExercise}
                        min={0}
                      />
                      <p>/{t("adminPanel.form.session")}</p>
                    </div>
                    {meta.error && meta.touched && <span>{t(meta.error)}</span>}
                  </div>
                );
              }}
            </Field>
            <div className={styles.service_images_wrap}>
              <Field
                type="file"
                name="iconImage"
                validate={!initialValues ? isRequired : undefined}
              >
                {({ input: { value, onChange, ...input }, meta }) => (
                  <div className={styles.input_preview_wrap}>
                    <div
                      className={`${styles.input_file_wrap} ${
                        meta.error && meta.touched
                          ? styles.form_input_error
                          : ""
                      }`}
                    >
                      <label htmlFor="iconImage">
                        {t("adminPanel.form.icon")}
                        {!initialValues && "*"}
                      </label>
                      <input
                        {...input}
                        onChange={({ target }) => {
                          onChange(target.files);
                          onSelectFile(target.files, "iconImage");
                        }}
                        type="file"
                        id="iconImage"
                        name="iconImage"
                        accept="image/*"
                      />
                      {meta.error && meta.touched && (
                        <span>{t(meta.error)}</span>
                      )}
                    </div>
                    {iconPreview && (
                      <div className={styles.preview}>
                        <img
                          src={iconPreview as string}
                          alt="downloaded_image"
                        />
                      </div>
                    )}
                  </div>
                )}
              </Field>
              <Field
                type="file"
                name="backgroundImage"
                validate={!initialValues ? isRequired : undefined}
              >
                {({ input: { value, onChange, ...input }, meta }) => (
                  <div className={styles.input_preview_wrap}>
                    <div
                      className={`${styles.input_file_wrap} ${
                        meta.error && meta.touched
                          ? styles.form_input_error
                          : ""
                      }`}
                    >
                      <label htmlFor="backgroundImage">
                        {t("adminPanel.form.backgroundImage")}{" "}
                        {!initialValues && "*"}
                      </label>
                      <input
                        {...input}
                        onChange={({ target }) => {
                          onChange(target.files);
                          onSelectFile(target.files, "backgroundImage");
                        }}
                        type="file"
                        id="backgroundImage"
                        name="backgroundImage"
                        accept="image/*"
                      />
                      {meta.error && meta.touched && (
                        <span>{t(meta.error)}</span>
                      )}
                    </div>
                    {backgroundPreview && (
                      <div className={styles.preview}>
                        <img
                          src={backgroundPreview as string}
                          alt="downloaded_image"
                        />
                      </div>
                    )}
                  </div>
                )}
              </Field>
            </div>
            <Field name="articleTitle">
              {({ input, meta }) => (
                <div className={`${styles.input_wrap} `}>
                  <label htmlFor="articleTitle">
                    {t("adminPanel.form.articleTitle")}
                  </label>
                  <input
                    {...input}
                    id="articleTitle"
                    type="text"
                    placeholder={t("adminPanel.form.articleTitle")}
                  />
                </div>
              )}
            </Field>
            <Field name="articleIntroduction">
              {({ input, meta }) => (
                <div className={`${styles.input_wrap} `}>
                  <label htmlFor="articleIntroduction">
                    {t("adminPanel.form.articleIntroduction")}
                  </label>
                  <textarea
                    {...input}
                    id="articleIntroduction"
                    placeholder={t("adminPanel.form.articleIntroduction")}
                  />
                </div>
              )}
            </Field>
            <Field name="articleFirstParagraph">
              {({ input, meta }) => (
                <div className={`${styles.input_wrap} `}>
                  <label htmlFor="articleFirstParagraph">
                    {t("adminPanel.form.articleFirstParagraph")}
                  </label>
                  <textarea
                    {...input}
                    id="articleFirstParagraph"
                    placeholder={t("adminPanel.form.articleFirstParagraph")}
                  />
                </div>
              )}
            </Field>
            <Field name="articleSecondParagraph">
              {({ input, meta }) => (
                <div className={`${styles.input_wrap} `}>
                  <label htmlFor="articleSecondParagraph">
                    {t("adminPanel.form.articleSecondParagraph")}
                  </label>
                  <textarea
                    {...input}
                    id="articleSecondParagraph"
                    placeholder={t("adminPanel.form.articleSecondParagraph")}
                  />
                </div>
              )}
            </Field>
            <Field name="articleConclusion">
              {({ input, meta }) => (
                <div className={`${styles.input_wrap} `}>
                  <label>{t("adminPanel.form.articleConclusion")}</label>
                  <textarea
                    {...input}
                    placeholder={t("adminPanel.form.articleConclusion")}
                  />
                </div>
              )}
            </Field>
            <Field type="file" name="articleImage">
              {({ input: { value, onChange, ...input }, meta }) => (
                <div className={styles.input_preview_wrap}>
                  <div className={styles.input_file_wrap}>
                    <label htmlFor="articleImage">
                      {t("adminPanel.form.articleImage")}
                    </label>
                    <input
                      {...input}
                      onChange={({ target }) => {
                        onChange(target.files);
                        onSelectFile(target.files, "articleImage");
                      }}
                      type="file"
                      id="articleImage"
                      name="articleImage"
                      accept="image/*"
                    />
                  </div>
                  {articlePreview && (
                    <div className={styles.preview}>
                      <img
                        src={articlePreview as string}
                        alt="downloaded_image"
                      />
                      <TableDeleteButton
                        onClick={() => {
                          setArticlePreview("");
                          form.change("articleImage", undefined);
                        }}
                      />
                    </div>
                  )}
                </div>
              )}
            </Field>
            {currentUser?.role === userRoles.admin && (
              <Button
                type="submit"
                className={"orange"}
                customStyle={styles.form_button}
                disabled={submitting}
                label={t("buttons.save")}
              />
            )}
          </form>
        )}
      />
    </div>
  );
};
